import { notification } from 'antd';
import log from 'loglevel';
import { assignUserRole,getUserLoggedInDetailsByEmail } from 'utils/services/users';
import * as userEventRoleService from 'utils/services/eventRoles';
import * as userTypes from 'shared/constants/userTypes';
import { formatUserEventRoleTypesList, formatEventAssignments } from 'utils/services/eventRoles/model';
import store from 'features/store';
import {
  loginRequestError,
  resetAuth,
  setAuthUserData
} from 'features/Authentication/redux/authActions';
import { verifySignUpSuccess } from 'features/RegistrationContainer/redux/registrationActions';

const getSignUpUrl = (signUpData) => {
  const { email, userId, code } = signUpData;
  return `/signup?verify_user=true&email=${email}&id=${userId}&code=${code}`;
};

export const handleAuthError = (error) => {
  store.dispatch(resetAuth());
  store.dispatch(loginRequestError(error));
};

export const processAuthResult = async (authResult) => {
  try {
    const { lastSignUp } = store.getState().auth;
    const { email } = authResult;
    if (!email) {
      const description = 'Authentication failed';
      notification.error({
        message: 'Error',
        description,
      });
      store.dispatch(loginRequestError(description));
      return;
    }
    if (lastSignUp.email && lastSignUp.assignRole) {
      notification.success({
        message: 'Success',
        description: 'Your new account setup successful. Please login.',
      });
      await assignUserRole(lastSignUp);
      store.dispatch(resetAuth());
      return;
    }
    // Don't dispatch loginRequestSuccess here — it sets authorized=true
    // before user data is loaded, causing premature redirects.
    // authorized=true is set at the end via setAuthUserData.
    
    log.debug('Fetching user details');
    let userResponse;
    try {
      userResponse = await getUserLoggedInDetailsByEmail(authResult.email);

      log.debug('User details fetched successfully');
    } catch (apiError) {
      log.error('Error fetching user details');
      const error = 'Unable to fetch user information. Please try again.';
      notification.error({
        message: 'Error',
        description: error,
      });
      store.dispatch(resetAuth());
      store.dispatch(loginRequestError(error));
      return;
    }
    
    const { userId, userType, partnerRegionsPrograms, ...userInfo } = userResponse || {};

    // Merge auth result data (picture, given_name, family_name) into userInfo
    const enrichedUserInfo = {
      ...userInfo,
      picture: authResult.picture || userInfo.picture,
      given_name: authResult.given_name || userInfo.given_name,
      family_name: authResult.family_name || userInfo.family_name,
    };

    if (!userId) {
      log.error('User not found in backend');
      const error = `Your email (${authResult.email}) is not registered in the system. Please contact your administrator or sign up first.`;
      notification.error({
        message: 'User Not Found',
        description: error,
        duration: 10, // Show for 10 seconds
      });
      store.dispatch(resetAuth());
      if (lastSignUp.email) {
        window.location.replace(getSignUpUrl(lastSignUp));
        return;
      }
      store.dispatch(loginRequestError(error));
      return;
    }
    if (lastSignUp.email && lastSignUp.email.toLowerCase() !== authResult.email.toLowerCase()) {
      const error = `Selected user account does not match signup email: ${lastSignUp.email}.`;
      store.dispatch(resetAuth());
      store.dispatch(loginRequestError(error));
      const returnTo = getSignUpUrl(lastSignUp);
      window.location.replace(returnTo);
      return;
    }
    if (lastSignUp.email) {
      notification.success({
        message: 'Success',
        description: 'Your new account setup successful.',
      });
      store.dispatch(verifySignUpSuccess());
    }

    const authUserData = {
      userInfo: enrichedUserInfo,
      userId,
      userType,
      loadingUser: false,
      authorized: true,
      loading: false,
      error: false
    };

    if (userType === userTypes.PARTNER_ADMIN && partnerRegionsPrograms &&  Array.isArray(partnerRegionsPrograms)) {
      Object.assign(authUserData, { partnerAdminRegionsPrograms: partnerRegionsPrograms });
    }

    // Todo: clean not used
    const { data, included } = await userEventRoleService.getUserEventRolesByUserId(userId);
    const assignedEvents = included.filter(i => i.type === 'event');
    const userEventAssignments = formatEventAssignments(data);
    const eventIds = assignedEvents.map(e => e.id);
    const signedInUserEventRoles = formatUserEventRoleTypesList(included);

    Object.assign(authUserData, {
      userEventRoles: signedInUserEventRoles,
      eventIdsAssociatedWithUser: eventIds,
      userEventAssignments
    });

    await store.dispatch(setAuthUserData(authUserData));
    return {
      userType,
      userEventRoles: signedInUserEventRoles,
      hasAcceptedTerms: enrichedUserInfo.hasAcceptedTerms
    };
  } catch (err) {
    log.error('Error in processAuthResult:', err);
    handleAuthError(err.description || err.message || 'Authentication error');
  }
};
